import { buildCategoryPageUrl } from '~~/helpers/category/buildCategoryPageUrl';
import type { Category } from '~~/types/commercetools';
import { ROUTES } from '~~/constants/routes';
import { createOnSaleLink } from '~~/helpers/navigation/createNavLinks';
import type { FooterLink } from '~~/types/cms/Footer/FooterLink/FooterLink';
import type { CountryCodeValue } from '~~/types/localization/localizationHelperTypes';
import { OUR_BRANDS_KEYS } from '~~/constants/brands';
import { getBrandInfo } from '~~/helpers/brand';

const getFirstLevelCategories = (categoriesContainer: Category[], countryCode: CountryCodeValue): FooterLink[] => {

  return categoriesContainer?.[0]?.children?.map((category) => ({
    name: category.name || '',
    url: category.slug
      ? buildCategoryPageUrl({
        category: category.slug,
        countryCode,
      })
      : '',
  })) || [];

};

type GetFooterLinksParams = {
  categoriesContainer: Category[];
  countryCode: CountryCodeValue;
  isExtraGuaranteeEnabled: boolean;
  isLoyaltyDiscountEnabled: boolean;
};

export const getFooterLinks = ({
  categoriesContainer,
  countryCode,
  isExtraGuaranteeEnabled,
  isLoyaltyDiscountEnabled,
}: GetFooterLinksParams) => {

  const CUSTOMER_SERVICE = [
    {
      translationKey: 'footer.Dispatch Information',
      routeKey: ROUTES.DISPATCH_INFORMATION,
      gtmId: 'footer-dispatch-information-link',
    },
    {
      translationKey: 'footer.Order tracking',
      routeKey: ROUTES.MY_ORDERS,
      gtmId: 'footer-order-tracking-link',
    },
    {
      translationKey: 'footer.My account',
      routeKey: ROUTES.MY_ACCOUNT,
      gtmId: 'footer-my-account-link',
    },
    {
      translationKey: 'footer.Withdrawal Policy',
      routeKey: ROUTES.WITHDRAWAL_POLICY,
      gtmId: 'footer-returns-and-cancellation-link',
    },
    ...(isExtraGuaranteeEnabled
      ? [{
        translationKey: 'footer.Premium Guarantee',
        routeKey: ROUTES.PREMIUM_GUARANTEE,
        gtmId: 'footer-premium-guarantee-link',
      }]
      : []
    ),
    {
      translationKey: 'footer.FAQs',
      routeKey: ROUTES.FAQ,
      gtmId: 'footer-faq-link',
    },
    {
      translationKey: 'footer.Secure payment methods',
      routeKey: ROUTES.PAYMENT_METHODS,
      gtmId: 'footer-payment-methods-link',
    },
  ];
  const ABOUT_US = [
    ...(isLoyaltyDiscountEnabled
      ? [{
        translationKey: 'footer.expondo Partners program',
        routeKey: ROUTES.EXPONDO_PARTNERS,
        gtmId: 'footer-expondo-partners-link',
      }]
      : []
    ),
    {
      translationKey: 'footer.Terms and conditions',
      routeKey: ROUTES.TERMS_CONDITIONS,
      gtmId: 'footer-terms-and-conditions-link',
    },
    {
      translationKey: 'footer.Privacy policy',
      routeKey: ROUTES.PRIVACY,
      gtmId: 'footer-privacy-policy-link',
    },
    {
      translationKey: 'footer.Cookies',
      routeKey: ROUTES.COOKIES,
      gtmId: 'footer-cookies-link',
    },
    {
      translationKey: 'footer.Company information',
      routeKey: ROUTES.COMPANY_INFORMATION,
      gtmId: 'footer-company-information-link',
    },
    {
      translationKey: 'footer.Jobs',
      routeKey: ROUTES.CAREER,
      gtmId: 'footer-jobs-link',
    },
    {
      translationKey: 'footer.Inspirations',
      routeKey: ROUTES.INSPIRATIONS,
      tag: 'footer.Blog',
      gtmId: 'footer-inspirations-link',
    },
  ];
  const OUR_PRODUCTS = [
    {
      translationKey: 'footer.Product recalls',
      routeKey: ROUTES.RECALLS,
      gtmId: 'footer-product-recalls-link',
    },
    ...getFirstLevelCategories(categoriesContainer, countryCode),
    {
      translationKey: 'footer.Sale',
      url: createOnSaleLink(),
      gtmId: 'footer-sale-link',
    },
    {
      translationKey: 'footer.Discount codes',
      routeKey: ROUTES.DISCOUNT_CODES,
      gtmId: 'footer-discount-codes-link',
    },
  ];

  const FOOTER_BRANDS = OUR_BRANDS_KEYS
    .map((brandKey) => {

      const { footerDisplayName, internalUrl, gtmId } = getBrandInfo(brandKey) || {};

      if (!footerDisplayName || !internalUrl) {

        return;

      }

      return {
        name: footerDisplayName,
        url: internalUrl,
        gtmId: `footer-${gtmId}-link`,
      };

    })
    .filter((brand): brand is { name: string, url: string, gtmId: string } => Boolean(brand));

  return {
    CUSTOMER_SERVICE,
    ABOUT_US,
    OUR_PRODUCTS,
    FOOTER_BRANDS,
  };

};
